/* eslint-disable angular/no-private-call */

import { defineValue, pluck } from '../utils/object-utils/object-utils';
import { pascalCase } from '../utils/string-utils';

const ACTIVE_STATIC_PROMISES = {};

export function _createCommonStaticMethods(
  DS,
  DSUtils,
  DSHttpAdapter,
  $q,
  $injector,
  autolist,
) {
  const _commonStaticMethods = {
    /**
     * Generates a complete URL for an endpoint on a JS-Data model
     * @param {string|Object?} id - The string ID or object with an ID of a model. If not included, then the URL would be on the collection
     * @param {string?} segment - The segment we want to check. Can be excluded if just looking for the base model URL
     * @param {*} options - Additional properties to pass to the DSHttpAdapter
     * @param {Object<String>} params - Additional (string-only) parameters to encode in the URL
     * @returns {string} The absolute URL
     */
    getEndpointUrl({ id, segment = null, options = {}, params = {} } = {}) {
      const Model = this;
      id = DSUtils.resolveId(Model, id);

      let url = DSUtils.makePath(
        options.basePath || DSHttpAdapter.defaults.basePath,
        DSHttpAdapter.getEndpoint(Model, id, options),
        id || segment,
        id && segment,
      );

      if (Object.keys(params).length) {
        url +=
          '?' +
          Object.keys(params)
            .map(
              (k) =>
                `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`,
            )
            .join('&');
      }

      return url;
    },

    saveOrCreate(instance, changes, options) {
      if (!instance) return $q.reject();
      let promise;
      const isNew = !instance.id;
      let newObj;
      const getInstanceChanges = (changes) => {
        const instanceChanges = angular.copy(changes);
        if (this.meta.excludeSaveOrCreateProps) {
          this.meta.excludeSaveOrCreateProps.forEach((prop) => {
            delete instanceChanges[prop];
          });
        }
        return instanceChanges;
      };
      if (isNew) {
        // for new items, the entire instance is our changes
        changes = angular.extend({}, instance, changes || {});
        promise = this.create(getInstanceChanges(changes), options).then(
          (o) => (newObj = o),
        );
      } else {
        // include any client-side changes in the model
        changes = angular.extend(
          {},
          ...Object.values(this.changes(instance)),
          changes || {},
        );
        const instanceChanges = getInstanceChanges(changes);
        const original = this.get(instance.id);
        const includeChange =
          this.meta.includeSaveOrCreateChange || (() => true);
        const hasChanges =
          original &&
          Object.keys(instanceChanges).some((k) => {
            return (
              includeChange(k, instanceChanges[k]) &&
              !angular.equals(original[k], instanceChanges[k])
            );
          });
        if (hasChanges || (changes.tag && changes.event)) {
          instanceChanges.$type = instance.$type;
          promise = this.update(instance.id, instanceChanges, options);
        } else {
          promise = $q.resolve(original);
        }
      }

      if (this.meta.afterSaveOrCreate) {
        promise = promise
          .then(this.meta.afterSaveOrCreate(instance, changes, options))
          .catch((err) => {
            if (isNew && newObj) {
              // send off a destroy if we fail on after-save events
              this.destroy(newObj);
            }
            return $q.reject(err);
          });
      }

      return promise;
    },

    //// old-style Find method

    /**
     * Enables making a server call to find the item at least once, even if it's already in the store
     *
     * @param {string} id - ID of item to get
     * @param {*} options - Options passed to get or find
     * @returns {Promise} Promise to be resolved if the item is found, or rejected if there was an error
     *    communicating with the server
     */
    findOnce: function (id, options) {
      const wasFound = this.markFoundOnce(id);

      // if we haven't already looked up the item, force a reload
      options = options || {};
      options.bypassCache = !wasFound;

      return this.find(id, options);
    },

    /**
     * Marks that this item has been looked up directly at least once. This means using find()
     * rather than findAll() or coming in as a nested item.
     * @param {string} id - ID of item
     * @returns {boolean} True if was found previously
     */
    markFoundOnce: function (id) {
      const foundOnce = (this.meta.foundOnce = this.meta.foundOnce || {});
      const wasFound = foundOnce[id];
      foundOnce[id] = true;
      return wasFound;
    },

    //// fetch methods

    /**
     * Fetches an item from either the backing store, or looks to the server if
     * not already present. Options will be passed to both the `get` and `find`
     * methods, but note that if this is requested with server-side parameters
     * yet already exists in the backing store, the parameters will be ignored.
     *
     * @param {string} id - ID of item to get
     * @param {*} options - Options passed to get or find
     * @returns {Promise} Promise to be resolved if the item is found, or rejected if there was an error
     *    communicating with the server
     */
    fetch: function (id, options) {
      const self = this;
      const bypassCache = !!(options && options.bypassCache);
      const model = bypassCache ? null : self.get(id, options);
      if (model) {
        if (options && options.rels) {
          return self.loadMissingRelations(model, options.rels);
        } else {
          return $q.when(model);
        }
      } else {
        return self.find(id, options);
      }
    },

    /**
     * Fetches an array of items from either the backing store, or loads from the server if the
     * requested search params return nothing.
     * @param {*} params - Parameters to search on
     * @param {*?} options - Any additional properties to pass along to the `findAll` method
     * @returns {Promise} Found models
     */
    fetchAll: function (params, options) {
      const self = this;
      const bypassCache = !!(options && options.bypassCache);
      const models = bypassCache ? [] : self.filter(params, options);
      // not optimal, because the filter might return the empty set correctly, but anyway...
      if (models.length) {
        return $q.when(models);
      } else {
        return self.findAll(params, options);
      }
    },

    /**
     * Fetches all models with the matching IDs. All IDs that are not already in the store are
     * grouped together into a single query to load missing items.
     *
     * @param {Array<string>} ids - IDs to load
     * @param {*?} options - Any additional properties to pass along to the `findAll` method
     * @param {boolean} options.bypassCache - If true, don't look in the local store
     * @returns {Promise} Result including all matching items.
     */
    fetchAllIds: function (ids, options = {}) {
      const self = this;
      const bypassCache = !!options.bypassCache;
      const searchIds = bypassCache ? ids : [];
      const found = [];
      if (!bypassCache) {
        ids.forEach((id) => {
          const item = self.get(id, options);
          if (item) {
            found.push(item);
          } else {
            searchIds.push(id);
          }
        });
      }
      return $q
        .when()
        .then(() => {
          if (searchIds.length) {
            const opts = {
              where: {
                id: { in: searchIds },
              },
              per: searchIds.length,
            };
            angular.merge(opts, options);
            return self.findAll(opts);
          } else {
            return [];
          }
        })
        .then((newItems) => {
          return found.concat(newItems);
        });
    },

    createWithFetch: function (attrs, options) {
      const k = 'createWithFetch:' + JSON.stringify(attrs);

      if (!ACTIVE_STATIC_PROMISES[k]) {
        options = angular.copy(options) || {};
        options.params = options.params || {};
        options.params.fetch = true;
        ACTIVE_STATIC_PROMISES[k] = this.create(attrs, options).finally(() => {
          delete ACTIVE_STATIC_PROMISES[k];
        });
      }

      return ACTIVE_STATIC_PROMISES[k];
    },

    /**
     * A specialized function designed to aid in finding items where only one or zero are
     * expected, and if zero are returned, then the item is created with the given attributes.
     *
     * @param params {object} Query parameters
     * @param attrs {object?} Attributes for the newly created item if the ID does not exist (params
     *    will be used if attrs is not provided)
     * @param options {object?} Options to be passed to fetchAll AND create
     * @returns {*} Promise A promise that will resolve with a single instance, OR reject if anything
     *    prevents a single instance from being created
     */
    fetchOrCreateWhere: function (params, attrs = null, options = {}) {
      const k = 'fetchOrCreateWhere:' + JSON.stringify(params);

      if (!ACTIVE_STATIC_PROMISES[k]) {
        const self = this;

        const create = () => {
          let method = options.createInstance ? 'createInstance' : 'create';
          return self[method](attrs || params || {}, options);
        };

        ACTIVE_STATIC_PROMISES[k] = self
          .fetchAll(params, options)
          .then((models) => {
            let resp = models.find((m) =>
              Object.keys(params).every((k) =>
                k === 'where' ? true : m[k] === params[k],
              ),
            );
            return resp || $q.reject({ status: 404 });
          })
          .catch((response) =>
            response.status === 404 ? create() : $q.reject(response),
          )
          .finally(() => {
            delete ACTIVE_STATIC_PROMISES[k];
          });
      }

      return ACTIVE_STATIC_PROMISES[k];
    },

    /**
     * A method that asynchronously loads any unknown items by creating a query that
     * excludes any known IDs.
     *
     * By default this method will only go to the server once, afterwards it returns `getAll`
     * wrapped in a promise. You can bypass this behavior by using the `bypassCache`
     *
     * Note: the results of this method will always contains _all_ the items in the system, even if
     * only some came from the server, which makes it a safe alternative to `fetchAll` or
     * `findAll`.
     *
     * @param {*} options Options to pass to findAll
     * @returns {Promise}
     */
    findMissing: function (options) {
      let self = this;
      if (self.meta.$$didFindMissing && !(options && options.bypassCache)) {
        return $q.when(self.getAll());
      } else {
        let where = {
          id: {
            notIn: self.getAll().map((item) => item.id),
          },
        };
        if (options.where) {
          angular.merge(where, options.where);
        }
        return self.findAll({ where }, options).then(() => {
          self.meta.$$didFindMissing = true;
          return self.getAll();
        });
      }
    },

    //// Relationships

    /**
     * Loads any missing default includes if they are not already defined on the instance.
     *
     * @param instance
     * @param rels Names of relations to load (see DI.loadRelations)
     * @returns {*} Promise
     */
    loadMissingRelations: function (instance, rels) {
      let self = this;
      let _rels = [];
      angular.forEach(rels, function (rel) {
        angular.forEach(self.relationList, function (fieldDef) {
          if (fieldDef.relation === rel && _rels.indexOf(rel) === -1) {
            let localField = fieldDef.localField;
            // force loading if the field is missing OR an empty array on hasMany
            if (
              localField &&
              (!instance[localField] ||
                (fieldDef.type === 'hasMany' && !instance[localField].length))
            ) {
              _rels.push(rel);
            }
          }
        });
      });
      return _rels.length
        ? self.loadRelations(instance, _rels)
        : $q.when(instance);
    },

    //// Meta Data

    /**
     * Asynchronously returns the meta data for this Model
     * @param {boolean} force If true, force reloading
     * @returns {*} Promise
     */
    getMetaData: function (force = false) {
      const self = this;

      if (self._metaDataPromise) return self._metaDataPromise;

      if (!force && self.metaData && self.metaData._loaded) {
        return $q.when(self.metaData);
      } else {
        if (!self.metaData) {
          self.metaData = {
            enums: {},
            flags: {},
            includes: [],
          };
        }
        const returnMeta = function () {
          self.metaData._loaded = true;
          delete self._metaDataPromise;
          return $q.when(self.metaData);
        };
        // reuse DS to handle the meta-data load
        self._metaDataPromise = self
          .find('meta', {
            cacheResponse: false,
            ignoreLoadingBar: true,
          })
          .then(returnMeta, returnMeta);

        return self._metaDataPromise;
      }
    },

    /**
     * Loads an enum array or enum value
     * @param {string} name Name of the enum (can include nested enums using `.`, as in `office.seating`)
     * @param {string} key (Optional) Looks up the mapped value of the enum based on the key
     * @param {boolean} async If true, the response will be a promise that resolves once metadata has been loaded
     * @returns {Promise|Array|string} Promise for async, array of enum objects if key is not provided, and a string if the key is provided.
     * This will always return a value, even if the metadata has not been loaded or we cannot find a matching enum or key
     */
    getEnum: function (name, key, async = false) {
      if (async) {
        return this.getMetaData().then(() => this.getEnum(name, key));
      } else {
        const enumData =
          this.metaData &&
          this.metaData.enums &&
          pluck(name, this.metaData.enums);
        const keyExists = key !== null && key !== undefined;

        if (!enumData) {
          // enable looking up the metadata so it's available on a later $digest cycle
          this.getMetaData();
          return keyExists ? '' : [];
        }

        if (keyExists) {
          if (!enumData.keyMap) {
            enumData.keyMap = enumData.reduce(function (map, item) {
              map[item.value] = item.text;
              return map;
            }, {});
          }
          return enumData.keyMap[key] || '';
        } else {
          return enumData;
        }
      }
    },
  };

  //--------------------------------------------------------------------
  //
  // Autolist functions
  //
  //--------------------------------------------------------------------

  ['getAll', 'filter', 'fetchAll'].forEach(function (fnName) {
    _commonStaticMethods['auto' + pascalCase(fnName)] = function (
      params,
      options,
    ) {
      const Model = this;
      return autolist.create(
        () => Model[fnName](params, options),
        Model,
        options,
      );
    };
  });

  // Auto find missing doesn't take any arguments
  _commonStaticMethods.autoFindMissing = function (options) {
    const Model = this;
    return autolist.create(() => Model.findMissing(options), Model, options);
  };

  // autoFindAll is treated special, so it can be used for server-side-only lists
  // It includes internal support for dynamic params and server-side pagination
  // (All other methods are assumed to be used for client-side handling)
  _commonStaticMethods.autoFindAll = function (params, options = {}) {
    const Model = this;
    let list = autolist.create(
      function autoFindAllAutoListMethod() {
        // get the query parameters (may be dynamic)
        const findAllParams = angular.isFunction(params) ? params() : params;
        // do not modify the original options
        const findAllOptions = angular.copy(options);

        // the following block is for tracking the full response data on the returned Array
        const responseDataCacheKey = JSON.stringify(findAllParams) || 'null';
        findAllOptions.responseDataCacheKey = responseDataCacheKey;
        const cacheResponse =
          findAllOptions.cacheResponse !== undefined
            ? findAllOptions.cacheResponse
            : Model.cacheResponse;
        const afterFn =
          cacheResponse === false ? 'afterFindAll' : 'afterInject';
        const originalAfterFn = findAllOptions[afterFn] || Model[afterFn];
        findAllOptions[afterFn] = function autoFindAllAfterFn(options, data) {
          let InjectedModel = options;
          while (InjectedModel && !InjectedModel.hasOwnProperty('$dsModel')) {
            // When injecting a nested data model, JS-Data decides to use the overridden `after*` method,
            // (meaning this one) rather than the one on the injected model
            // So we need to look for the correct model for the injected resource by walking the prototype
            // chain
            InjectedModel = Object.getPrototypeOf(InjectedModel);
          }

          if (InjectedModel.name !== Model.name) {
            // this is a different model than the one the method was called on, use the default after*
            // function for that model
            if (InjectedModel[afterFn]) {
              InjectedModel[afterFn](InjectedModel, data);
            }
          } else {
            // this is the top-level model, so we can use the original method instead
            if (originalAfterFn) {
              originalAfterFn(options, data);
            }
            if (Array.isArray(data) && Model.$responseData) {
              // store the response data on the final output array from the find all operation
              // we do it this way because the result of Model.findAll can be returned to *different*
              // requests to findAll! Even if they come from different sources, if the params match,
              // JS-Data will only make a single request at a time.
              data.$responseData = Model.$responseData[responseDataCacheKey];
              delete Model.$responseData[responseDataCacheKey];
            }
          }
          return data;
        };

        if (
          findAllOptions.bypassCache !== false &&
          findAllOptions.cacheResponse !== true
        ) {
          findAllOptions.bypassCache = true;
        }

        return Model.findAll(findAllParams, findAllOptions).then((resp) => {
          // catch pagination errors if we have no data
          if (resp.length === 0 && findAllParams && findAllParams.page > 1) {
            return Model.findAll(
              angular.extend({}, findAllParams, { page: 1 }),
              findAllOptions,
            );
          }
          // store the response data back on the autolist for pagination
          list.responseData = resp.$responseData;
          return resp;
        });
      },
      Model,
      options,
    );

    // configure default server-side pagination
    list.paginate(function autoFindAllPagination() {
      let data = { client: false };
      if (
        list.responseData &&
        list.responseData.params &&
        list.responseData.params.per
      ) {
        data.total = list.responseData.totalCount;
        data.perPage = list.responseData.params.per;
        data.page = list.responseData.currentPage;
      }
      return data;
    });

    return list;
  };

  /**
   * A variation of autoFindAll and findAll that recursively loads every matching item across multiple pages
   * Use the 3rd parameter to `then` to receive progress notifications
   *
   * @param params {Object}
   * @param params.per {number} By default this is 100, but can be adjusted based on needs
   * @param options
   * @returns {Promise}
   */
  _commonStaticMethods.retrieveAll = function (params = {}, options = {}) {
    const deferred = $q.defer();
    params.per = params.per || 100;
    params.page = 1;

    const cfpLoadingBar =
      $injector.has('cfpLoadingBar') && $injector.get('cfpLoadingBar');

    let showProgress = !options.ignoreLoadingBar && cfpLoadingBar;
    if (showProgress) cfpLoadingBar.start();
    options.ignoreLoadingBar = true;

    const result = [];
    const autolist = this.autoFindAll(params, options);
    const _processNext = () => {
      autolist.update().promise.then((list) => {
        result.push(...list);
        if (list.pg.hasNext) {
          const retrieved = result.length;
          const total = list.pg.total;
          const progress = retrieved / total;
          deferred.notify({ progress, retrieved, total });
          if (showProgress) cfpLoadingBar.set(progress);

          params.page += 1;
          _processNext();
        } else {
          if (showProgress) cfpLoadingBar.complete();
          autolist.destroy();
          deferred.resolve(result);
        }
      }, deferred.reject);
    };
    _processNext();

    return deferred.promise;
  };

  // Attach static methods to DS defaults for simple inheritance
  angular.forEach(_commonStaticMethods, (fn, key) => {
    defineValue(DS.defaults, key, fn);
  });
}
